import { LegalPage, H, P, LI } from "@/components/site/Legal";

export default function PrivacyPolicy() {
  return (
    <LegalPage title="Privacy Policy" updated="June 2026" testId="privacy-policy-page">
      <P>
        PinQuests ("PinQuests," "we," "us," or "our") operates PinQuests.com, a location-based
        storytelling and scavenger-hunt platform. This Privacy Policy explains what information we
        collect, how we use it, and the choices you have. By using PinQuests, you agree to this policy.
      </P>

      <H>1. Information We Collect</H>
      <P>Information you provide directly:</P>
      <ul>
        <LI>Account and enrollment information: name, mobile phone number, and optional email address.</LI>
        <LI>Content you create: quest titles, clues, photos, videos, audio, messages, and target locations you place on the map.</LI>
        <LI>Communications you send to us.</LI>
      </ul>
      <P>Information collected when you play:</P>
      <ul>
        <LI>Submitted guess locations. When you answer a clue — whether by dropping a pin on the map or by using your device's current location — we record the single coordinate you chose to submit, the distance from the target, the time of submission, and whether the attempt succeeded.</LI>
        <LI>Basic technical data such as browser type and general usage analytics.</LI>
      </ul>
      <P>
        We do not continuously track your location. Device location is accessed only at the moment you
        tap "Check My Location," only with your permission, and only the submitted coordinate is stored.
      </P>

      <H>2. The 24-Hour Location Delay</H>
      <P>
        Location-related activity (successful and failed guesses, coordinates, distances, and attempt
        maps) is withheld from quest creators for at least 24 hours after submission. This delay is
        enforced on our servers. Notifications derived from location activity respect the same delay.
      </P>

      <H>3. Guess Method Confidentiality</H>
      <P>
        PinQuests never discloses to a quest creator — or anyone else — whether a submitted location
        came from your physical device location or from a pin you placed manually on the map. A
        submitted coordinate does not prove, and must never be treated as proving, physical presence.
      </P>

      <H>4. How We Use Information</H>
      <ul>
        <LI>To operate the service: validating guesses, unlocking rewards, scheduling clues, and delivering quests.</LI>
        <LI>To send service messages, such as your early-access invitation or notice that a new clue is available.</LI>
        <LI>To keep the platform safe: reviewing reported content and preventing abuse.</LI>
        <LI>To improve PinQuests through aggregate, de-identified analytics. We do not use your precise location data for advertising.</LI>
      </ul>

      <H>5. How We Share Information</H>
      <ul>
        <LI>With quest creators: after the 24-hour delay, the creator of a quest you play may see the locations you submitted for that quest, including unsuccessful attempts. This is disclosed to you before you begin any quest.</LI>
        <LI>With service providers who host our infrastructure, storage, maps, and messaging, bound by confidentiality obligations.</LI>
        <LI>When required by law, or to protect the rights, safety, or property of PinQuests or others.</LI>
      </ul>
      <P>We do not sell your personal information.</P>

      <H>6. Your Content and Media</H>
      <P>
        Photos, videos, and other media inside private quests are stored privately and delivered only
        to invited participants. Private quests are accessible only through secure, unguessable
        invitation links and are not publicly listed or enumerable.
      </P>

      <H>7. Children</H>
      <P>
        PinQuests is not directed to children under 13, and children under 13 may not create accounts.
        Minors may participate in quests only with the consent and supervision of a parent or guardian,
        who is responsible for reviewing quest content and locations before play.
      </P>

      <H>8. Data Retention and Deletion</H>
      <P>
        We retain information for as long as needed to provide the service. You may request deletion of
        your account, your quests, or your submitted attempt history by contacting us at
        privacy@pinquests.com. Some records may be retained where required for legal or safety reasons.
      </P>

      <H>9. Security</H>
      <P>
        We use HTTPS everywhere, server-side validation of all location submissions, secure invitation
        tokens, and access controls that restrict and audit internal access to sensitive location data.
        No system is perfectly secure, but privacy is part of our architecture, not an afterthought.
      </P>

      <H>10. Your Choices</H>
      <ul>
        <LI>You may play eligible clues entirely from the map without ever granting device location access.</LI>
        <LI>You can decline location permissions in your browser or device at any time.</LI>
        <LI>You may opt out of non-essential communications.</LI>
      </ul>

      <H>11. Changes to This Policy</H>
      <P>
        We may update this policy as PinQuests evolves. We will post the updated version here and, for
        material changes, provide additional notice.
      </P>

      <H>12. Contact</H>
      <P>Questions? Contact privacy@pinquests.com.</P>
    </LegalPage>
  );
}
